import { COMPANION, getCompanionBondTier, getCompanionProfile } from './companion.js';

export const VISION = Object.freeze({ range: 14, halfAngle: Math.PI * 0.42 });
export const HEARING = Object.freeze({ baseRadius: 4, noiseRange: 12, minimumNoise: 0.05, searchDuration: 3.2, evidenceCadence: 0.6 });
export const MELEE = Object.freeze({ windup: 0.38, contact: 0.16, recovery: 0.5, range: 2.25, lungeDistance: 0.85, lockRate: 22, parryWindow: 0.22, parryStamina: 18, parryCooldown: 0.8, parryResolve: 8 });
export const FEEL = Object.freeze({
  acceleration: 24,
  deceleration: 28,
  turnRate: 20,
  actionBuffer: 0.6,
  dodgeCooldown: 0.58,
  // A light connect used to stop the world for 35 ms — under two frames at 60 Hz, which reads as a
  // number going up rather than as a blade landing. These are the values that make contact cost
  // time, and the parry is the best thing that can happen in a fight, so it stops the longest.
  hitStopLight: 0.05,
  hitStopHeavy: 0.085,
  hitStopParry: 0.115,
  lightAttack: Object.freeze({ windup: 0.11, contact: 0.07, recovery: 0.16 }),
  heavyAttack: Object.freeze({ windup: 0.18, contact: 0.1, recovery: 0.25 }),
});

export function getOnboardingDwellMs(action) {
  return action === 'move' || action === 'sneak' ? 450 : 0;
}
export const BLOODLINES = Object.freeze({
  human: Object.freeze({ label: 'Human', speed: 1, visionRange: 1, detectionRate: 1, hearingRate: 1, noise: 1, staminaRegen: 8, sprintStaminaDrain: 18, sprintBloodDrain: 0, lightDamage: 1, heavyDamage: 1 }),
  vampire: Object.freeze({ label: 'Vampire', speed: 1.08, visionRange: 1.24, detectionRate: 0.68, hearingRate: 0.85, noise: 0.82, staminaRegen: 5, sprintStaminaDrain: 10, sprintBloodDrain: 6, lightDamage: 1.18, heavyDamage: 1.24 }),
  werewolf: Object.freeze({ label: 'Werewolf', speed: 1.32, visionRange: 0.92, detectionRate: 1.42, hearingRate: 1.18, noise: 1.2, staminaRegen: 12, sprintStaminaDrain: 26, sprintBloodDrain: 0, lightDamage: 1.32, heavyDamage: 1.42 }),
});
export const MOONBOUND = Object.freeze({
  linkRange: 9,
  telegraph: 1.35,
  recovery: 1.1,
  cooldown: 6.5,
  maxTargets: 2,
  linkedWindup: 0.24,
  linkedCooldown: 0.35,
  linkDuration: 2.4,
  holdDuration: 0.9,
  attackDamage: 22,
  linkResolve: 12,
});

const BLOODLINE_ORDER = Object.freeze(['human', 'vampire', 'werewolf']);
const BLOODLINE_COSTS = Object.freeze({
  vampire: Object.freeze({ blood: 15, stamina: 0 }),
  werewolf: Object.freeze({ blood: 0, stamina: 25 }),
  human: Object.freeze({ blood: 0, stamina: 20 }),
});
const MELEE_PHASE_ORDER = Object.freeze(['windup', 'contact', 'recovery']);
const MAX_GAMEPLAY_VALUE = Number.MAX_SAFE_INTEGER;

function bounded(value, minimum, maximum, fallback = minimum) {
  return Math.min(maximum, Math.max(minimum, Number.isFinite(value) ? value : fallback));
}

export function damp(current, target, response, dt) {
  const from = Number.isFinite(current) ? current : 0;
  const to = Number.isFinite(target) ? target : 0;
  const rate = Math.max(0, Number.isFinite(response) ? response : 0);
  const delta = Math.max(0, Number.isFinite(dt) ? dt : 0);
  if (!rate || !delta) return from;
  return from + (to - from) * (1 - Math.exp(-rate * delta));
}

export function dampAngle(current, target, response, dt) {
  const from = Number.isFinite(current) ? current : 0;
  const to = Number.isFinite(target) ? target : 0;
  const difference = Math.atan2(Math.sin(to - from), Math.cos(to - from));
  return from + difference * (1 - Math.exp(-Math.max(0, response) * Math.max(0, dt)));
}

export function bufferAction(current, action, elapsed, duration = FEEL.actionBuffer) {
  if (!action || !Number.isFinite(elapsed)) return null;
  const window = Math.max(0, Number.isFinite(duration) ? duration : 0);
  if (!current || current.action !== action) return { action, expiresAt: elapsed + window };
  return { action, expiresAt: elapsed + window };
}

export function consumeBufferedAction(buffer, elapsed) {
  if (!buffer || !Number.isFinite(buffer.expiresAt) || !Number.isFinite(elapsed) || elapsed > buffer.expiresAt) return null;
  return buffer.action;
}

export function getCameraCollisionScale(fromX, fromZ, toX, toZ, blockers = [], padding = 0.4) {
  const ax = Number.isFinite(fromX) ? fromX : 0;
  const az = Number.isFinite(fromZ) ? fromZ : 0;
  const bx = Number.isFinite(toX) ? toX : ax;
  const bz = Number.isFinite(toZ) ? toZ : az;
  const dx = bx - ax;
  const dz = bz - az;
  const lengthSq = dx * dx + dz * dz;
  if (lengthSq <= 0.0001) return 1;
  let nearest = 1;
  for (const blocker of Array.isArray(blockers) ? blockers : []) {
    const radius = Math.max(0, Number(blocker?.radius) || 0) + Math.max(0, Number(padding) || 0);
    const fx = ax - (Number(blocker?.x) || 0);
    const fz = az - (Number(blocker?.z) || 0);
    const a = lengthSq;
    const b = 2 * (fx * dx + fz * dz);
    const c = fx * fx + fz * fz - radius * radius;
    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0) continue;
    const root = Math.sqrt(discriminant);
    const entry = (-b - root) / (2 * a);
    const exit = (-b + root) / (2 * a);
    if (exit < 0.08 || entry > nearest) continue;
    nearest = Math.max(0.2, entry < 0.08 ? exit : entry);
  }
  return Math.min(1, nearest);
}

function evidencePosition(position) {
  return {
    x: bounded(position?.x, -MAX_GAMEPLAY_VALUE, MAX_GAMEPLAY_VALUE, 0),
    z: bounded(position?.z, -MAX_GAMEPLAY_VALUE, MAX_GAMEPLAY_VALUE, 0)
  };
}

export function getNoiseRadius(noise, options = {}) {
  const minimumNoise = bounded(options.minimumNoise, 0, 1, HEARING.minimumNoise);
  const baseRadius = bounded(options.baseRadius, 0, MAX_GAMEPLAY_VALUE, HEARING.baseRadius);
  const noiseRange = bounded(options.noiseRange, 0, MAX_GAMEPLAY_VALUE, HEARING.noiseRange);
  const level = bounded(noise, 0, 1, 0);
  return level < minimumNoise ? 0 : bounded(baseRadius + level * noiseRange, 0, MAX_GAMEPLAY_VALUE, baseRadius);
}

function segmentCircleHit(ax, az, bx, bz, cx, cz, radius) {
  const dx = bx - ax;
  const dz = bz - az;
  const lengthSq = dx * dx + dz * dz;
  const t = lengthSq ? Math.max(0, Math.min(1, ((cx - ax) * dx + (cz - az) * dz) / lengthSq)) : 0;
  const x = ax + dx * t;
  const z = az + dz * t;
  return Math.hypot(x - cx, z - cz) < radius;
}

export function hasLineOfSight(from, to, blockers = []) {
  if (!from || !to) return false;
  return !(Array.isArray(blockers) ? blockers : []).some(blocker => {
    const radius = bounded(blocker?.radius || 0.55, 0, MAX_GAMEPLAY_VALUE, 0.55);
    return segmentCircleHit(from.x, from.z, to.x, to.z, blocker?.x, blocker?.z, radius);
  });
}
export function canHearNoise(observer, source, noise, options = {}) {
  if (!observer || !source) return false;
  const level = bounded(noise, 0, 1, 0);
  const minimumNoise = bounded(options.minimumNoise, 0, 1, HEARING.minimumNoise);
  if (level < minimumNoise) return false;
  const distance = Math.hypot(source.x - observer.x, source.z - observer.z);
  return Number.isFinite(distance) && distance <= getNoiseRadius(level, options);
}

export function getBloodlineProfile(id) {
  return BLOODLINES[id] || BLOODLINES.human;
}

export function getNextBloodline(id) {
  const index = Math.max(0, BLOODLINE_ORDER.indexOf(id));
  return BLOODLINE_ORDER[(index + 1) % BLOODLINE_ORDER.length];
}

export function planBloodlineTransition(current, target, resources = {}) {
  if (!BLOODLINES[current]) return { ok: false, reason: 'unknown' };
  const to = BLOODLINES[target] ? target : getNextBloodline(current);
  if (to === current) return { ok: false, reason: 'unchanged', from: current, to, cost: { blood: 0, stamina: 0 } };
  const cost = BLOODLINE_COSTS[to];
  if ((resources.blood ?? Infinity) < cost.blood) return { ok: false, reason: 'blood', from: current, to, cost };
  if ((resources.stamina ?? Infinity) < cost.stamina) return { ok: false, reason: 'stamina', from: current, to, cost };
  return { ok: true, from: current, to, cost };
}

export function stealthExposureRate({ visible = false, audible = false, distance = Infinity, noise = 0, sneaking = false, sprinting = false, bloodline = 'human', exposureScale = 1 }) {
  const profile = getBloodlineProfile(bloodline);
  let rate = 0;
  if (visible) rate = 0.34 + noise * 0.24 + (distance < 4 ? 0.18 : 0);
  else if (audible) rate = 0.12 + noise * 0.22;
  if (!rate) return 0;
  const stance = sneaking ? (sprinting ? 0.46 : 0.58) : sprinting ? 1.35 : 1;
  const scale = bounded(exposureScale, 0, MAX_GAMEPLAY_VALUE, 1);
  return rate * stance * (visible ? profile.detectionRate : profile.hearingRate) * scale;
}

export function perceiveTarget(observer, target, blockers = [], options = {}) {
  return {
    visible: canSeeTarget(observer, target, blockers, options.vision),
    audible: Boolean(observer && target && canHearNoise(observer, target, options.noise ?? 0, options.hearing))
  };
}


export function canSeeTarget(observer, target, blockers = [], options = {}) {
  if (!observer || !target) return false;
  const range = bounded(options.range, 0, MAX_GAMEPLAY_VALUE, VISION.range);
  const halfAngle = bounded(options.halfAngle, 0, Math.PI, VISION.halfAngle);
  const dx = target.x - observer.x;
  const dz = target.z - observer.z;
  const distance = Math.hypot(dx, dz);
  if (!Number.isFinite(distance)) return false;
  if (distance > range || distance < 0.001) return distance <= range;
  const facing = Math.atan2(dx, dz);
  const rotation = Number.isFinite(observer.rotation?.y) ? observer.rotation.y : Number.isFinite(observer.rotation) ? observer.rotation : 0;
  const difference = Math.atan2(Math.sin(facing - rotation), Math.cos(facing - rotation));
  return Math.abs(difference) <= halfAngle && hasLineOfSight(observer, target, blockers);
}

export function getGuardEvidenceTransition(data, input) {
  if (input.visible) return 'visual';
  const audible = input.audible ?? Boolean(input.observer && canHearNoise(input.observer, input.playerPosition, input.noise ?? 0, input.hearing));
  if (audible) return 'audible';
  if (!data.investigating) return 'search';
  const searchDuration = bounded(input.searchDuration ?? input.hearing?.searchDuration, 0, MAX_GAMEPLAY_VALUE, HEARING.searchDuration);
  const elapsed = bounded(input.elapsed, 0, MAX_GAMEPLAY_VALUE, 0);
  const lastEvidenceAt = Number.isFinite(data.lastEvidenceAt) ? data.lastEvidenceAt : -Infinity;
  return elapsed - lastEvidenceAt >= searchDuration ? 'expired' : 'search';
}

export function updateGuardAwareness(data, input) {
  const transition = getGuardEvidenceTransition(data, input);
  const evidenceCadence = bounded(input.evidenceCadence ?? input.hearing?.evidenceCadence, 0, MAX_GAMEPLAY_VALUE, HEARING.evidenceCadence);
  const elapsed = bounded(input.elapsed, 0, MAX_GAMEPLAY_VALUE, 0);
  const dt = bounded(input.dt, 0, MAX_GAMEPLAY_VALUE, 0);
  const distance = bounded(input.distance, 0, MAX_GAMEPLAY_VALUE, MAX_GAMEPLAY_VALUE);
  const noise = bounded(input.noise, 0, 1, 0);
  const exposureRate = stealthExposureRate({ visible: transition === 'visual', audible: transition === 'audible', distance, noise, sneaking: input.sneak, sprinting: input.sprinting, bloodline: input.bloodline, exposureScale: input.exposureScale });
  const alertness = bounded(data.alertness, 0, 1, 0);
  if (transition === 'visual') {
    data.alertness = bounded(alertness + dt * exposureRate, 0, 1, alertness);
    data.lastSeen = evidencePosition(input.playerPosition);
    data.lastKnown = data.lastSeen;
    data.lastEvidenceAt = elapsed;
    data.hasVisualContact = true;
    data.investigating = true;
  } else if (transition === 'audible') {
    const lastHeardAt = Number.isFinite(data.lastHeardAt) ? data.lastHeardAt : -Infinity;
    const canRefresh = !data.investigating || data.lastHeard == null || elapsed - lastHeardAt >= evidenceCadence;
    data.alertness = bounded(alertness + dt * exposureRate, 0, 1, alertness);
    if (canRefresh) {
      data.lastHeard = evidencePosition(input.playerPosition);
      data.lastHeardAt = elapsed;
      data.lastKnown = data.lastHeard;
      data.lastEvidenceAt = elapsed;
    }
    data.hasVisualContact = false;
    data.investigating = true;
  } else {
    data.alertness = bounded(alertness - dt * (input.sneak ? 0.12 : 0.2), 0, 1, alertness);
    data.hasVisualContact = false;
    if (transition === 'expired') {
      data.investigating = false;
      data.lastSeen = null;
      data.lastHeard = null;
      data.lastKnown = null;
      data.lastEvidenceAt = -Infinity;
      data.lastHeardAt = -Infinity;
    }
  }
  return data.investigating ? data.lastKnown : null;
}

export function advanceMeleePhase(phase, timer, dt, options = {}) {
  const windup = bounded(options.windup, 0, MAX_GAMEPLAY_VALUE, MELEE.windup);
  const contact = bounded(options.contact, 0, MAX_GAMEPLAY_VALUE, MELEE.contact);
  const recovery = bounded(options.recovery, 0, MAX_GAMEPLAY_VALUE, MELEE.recovery);
  let index = MELEE_PHASE_ORDER.indexOf(phase);
  let current = index >= 0 ? phase : 'idle';
  let remaining = bounded(timer, 0, MAX_GAMEPLAY_VALUE, 0);
  let elapsed = bounded(dt, 0, MAX_GAMEPLAY_VALUE, 0);
  let contactReached = false;
  while (index >= 0) {
    const duration = current === 'windup' ? windup : current === 'contact' ? contact : recovery;
    if (elapsed < remaining) break;
    elapsed -= remaining;
    if (current === 'windup') contactReached = true;
    if (index === MELEE_PHASE_ORDER.length - 1) {
      current = 'idle';
      remaining = 0;
      break;
    }
    index += 1;
    current = MELEE_PHASE_ORDER[index];
    remaining = current === 'windup' ? windup : current === 'contact' ? contact : recovery;
  }
  return {
    phase: current,
    timer: current === 'idle' ? 0 : bounded(remaining - elapsed, 0, MAX_GAMEPLAY_VALUE, 0),
    contactReached
  };
}

export function isParryActive(data, elapsed) {
  if (!data || bounded(data.parryTimer, 0, MELEE.parryWindow, 0) <= 0) return false;
  const time = bounded(elapsed, 0, MAX_GAMEPLAY_VALUE, 0);
  const startedAt = bounded(data.parryStartedAt, 0, MAX_GAMEPLAY_VALUE, time);
  return time >= startedAt && time - startedAt <= MELEE.parryWindow;
}

export function beginParry(data, elapsed) {
  const cooldown = bounded(data.parryCooldown, 0, MAX_GAMEPLAY_VALUE, 0);
  const timer = bounded(data.parryTimer, 0, MELEE.parryWindow, 0);
  const stamina = bounded(data.stamina ?? 100, 0, 100, 100);
  if (cooldown > 0 || timer > 0 || stamina < MELEE.parryStamina) return false;
  const time = bounded(elapsed, 0, MAX_GAMEPLAY_VALUE, 0);
  data.stamina = bounded(stamina - MELEE.parryStamina, 0, 100, stamina);
  data.parryTimer = MELEE.parryWindow;
  data.parryCooldown = MELEE.parryCooldown;
  data.parryStartedAt = time;
  data.lastTick = time;
  return true;
}

export function resolveParry(data, elapsed) {
  if (bounded(data.parryTimer, 0, MELEE.parryWindow, 0) > 0) {
    const time = bounded(elapsed, 0, MAX_GAMEPLAY_VALUE, 0);
    const previousTick = Number.isFinite(data.lastTick) ? data.lastTick : Number.isFinite(data.parryStartedAt) ? data.parryStartedAt : time;
    data.parryTimer = bounded(data.parryTimer - bounded(time - previousTick, 0, MAX_GAMEPLAY_VALUE, 0), 0, MELEE.parryWindow, 0);
    data.lastTick = time;
  }
  return data.parryTimer > 0;
}
export function settleMeleeRecovery(data) {
  if (!data || data.attackPhase !== 'recovery') return false;
  data.attackPhase = 'idle';
  data.attackTimer = 0;
  data.attackTotal = 0;
  data.state = data.movement > 0 ? 'move' : 'idle';
  return true;
}
function entityData(value) {
  return value?.userData ?? value;
}

function entityPosition(value) {
  return value?.position ?? value;
}

function planarDistance(first, second) {
  const dx = Number(first?.x) - Number(second?.x);
  const dz = Number(first?.z) - Number(second?.z);
  return Number.isFinite(dx) && Number.isFinite(dz) ? Math.hypot(dx, dz) : Infinity;
}

function selectLinkTargets(data, allies, origin = data.position ?? data.home ?? data) {
  const candidates = [];
  for (let index = 0; index < allies.length; index += 1) {
    const ally = entityData(allies[index]);
    const position = entityPosition(allies[index]);
    if (!ally || ally === data || ally.dead || ally.kind === 'marshal' || ally.kind === 'boss') continue;
    const distance = planarDistance(position, origin);
    if (distance > MOONBOUND.linkRange) continue;
    candidates.push({ data: ally, distance, index, id: ally.id || `ally-${index}` });
  }
  candidates.sort((first, second) => first.distance - second.distance || String(first.id).localeCompare(String(second.id)) || first.index - second.index);
  return candidates.slice(0, MOONBOUND.maxTargets);
}

export function updateMoonboundMarshal(data, input = {}) {
  if (!data || data.kind !== 'marshal' || data.dead) return { phase: 'idle', timer: 0, event: null, targets: [] };
  const dt = bounded(input.dt, 0, MAX_GAMEPLAY_VALUE, 0);
  const elapsed = bounded(input.elapsed, 0, MAX_GAMEPLAY_VALUE, 0);
  const alertness = bounded(input.alertness ?? data.alertness, 0, 1, 0);
  const allies = Array.isArray(input.allies) ? input.allies : [];
  data.linkCooldown = Math.max(0, (data.linkCooldown || 0) - dt);
  if (!['idle', 'telegraph', 'recovery'].includes(data.linkPhase)) data.linkPhase = 'idle';
  if (!Number.isFinite(data.linkTimer) || data.linkTimer < 0) data.linkTimer = 0;
  if (data.linkPhase === 'idle') {
    if (data.linkCooldown > 0 || alertness < 0.45) return { phase: 'idle', timer: 0, event: null, targets: [] };
    const targets = selectLinkTargets(data, allies, input.position ?? data.position ?? data.home ?? data);
    if (!targets.length) return { phase: 'idle', timer: 0, event: null, targets: [] };
    data.linkPhase = 'telegraph';
    data.linkTimer = MOONBOUND.telegraph;
    data.linkTargetRefs = targets.map((target) => target.data);
    data.linkTargetIds = targets.map((target) => target.id);
    return {
      phase: 'telegraph',
      timer: data.linkTimer,
      event: { type: 'telegraph', targetIds: [...data.linkTargetIds] },
      targets,
    };
  }
  let remaining = dt;
  let event = null;
  let guard = 0;
  while (data.linkPhase !== 'idle' && guard < 4) {
    guard += 1;
    if (data.linkTimer > 0) {
      if (remaining <= 0) break;
      const consumed = Math.min(remaining, data.linkTimer);
      data.linkTimer -= consumed;
      remaining -= consumed;
      if (data.linkTimer > 0) break;
    }
    if (data.linkPhase === 'telegraph') {
      const targets = Array.isArray(data.linkTargetRefs) ? data.linkTargetRefs : [];
      targets.forEach((target, index) => {
        if (!target || target.dead) return;
        if (index === 0) {
          target.linkUntil = elapsed + MOONBOUND.linkDuration;
          target.linkWindup = MOONBOUND.linkedWindup;
          target.linkCharge = 1;
          target.attackCooldown = Math.min(target.attackCooldown || MOONBOUND.linkedCooldown, MOONBOUND.linkedCooldown);
        } else {
          target.linkHoldUntil = elapsed + MOONBOUND.holdDuration;
          target.linkHold = true;
          target.attackPhase = 'recovery';
          target.attackTimer = MELEE.recovery;
          target.attackCooldown = Math.max(target.attackCooldown || 0, MOONBOUND.holdDuration);
        }
      });
      data.linkCount = (data.linkCount || 0) + 1;
      data.linkPhase = 'recovery';
      data.linkTimer = MOONBOUND.recovery;
      data.linkCooldown = MOONBOUND.cooldown;
      event = { type: 'linked', targetIds: [...(data.linkTargetIds || [])], primaryId: data.linkTargetIds?.[0] || null, holdIds: data.linkTargetIds?.slice(1) || [] };
    } else if (data.linkPhase === 'recovery') {
      data.linkPhase = 'idle';
      data.linkTimer = 0;
      event = { type: 'complete', targetIds: [...(data.linkTargetIds || [])] };
      break;
    }
  }
  return { phase: data.linkPhase, timer: data.linkTimer, event, targets: [] };
}

export function isMinimapTickDue(accumulator, interval = 0.1) {
  return Number.isFinite(accumulator) && Number.isFinite(interval) && interval > 0 && accumulator >= interval;
}



export function commandCompanion({ player, mara, enemies = [], interactables = [], now = 0, cooldown = 0, bond = 0, revealDuration = null }) {
  if (cooldown > 0 || !player || !mara) return { ok: false, reason: cooldown > 0 ? 'cooldown' : 'unavailable' };
  let target = null;
  let best = Infinity;
  let bestPriority = 3;
  for (const enemy of enemies) {
    if (enemy.userData?.dead) continue;
    const distance = player.position.distanceTo(enemy.position);
    // A snuff channel is the one thing in Vespera that finishes on its own clock, so it outranks a
    // windup: naming it is the only way to stop a hearth without standing on the bowl.
    const priority = enemy.userData?.snuffPhase === 'channel' ? 0
      : enemy.userData?.linkPhase === 'telegraph' ? 0
        : enemy.userData?.attackPhase === 'windup' ? 1 : 2;
    if (priority < bestPriority || (priority === bestPriority && distance < best)) { best = distance; bestPriority = priority; target = { type: 'enemy', object: enemy, position: enemy.position, label: enemy.userData?.name || 'Enemy' }; }
  }
  for (const item of interactables) {
    if (item.userData?.collected) continue;
    const distance = player.position.distanceTo(item.position);
    if (!target && distance < best) { best = distance; target = { type: item.userData?.type || 'interactable', object: item, position: item.position, label: item.userData?.label || 'Interactable' }; }
  }
  if (!target) return { ok: false, reason: 'empty' };
  const profile = getCompanionProfile(bond);
  if (target.type === 'enemy') {
    const enemy = target.object;
    const data = enemy.userData;
    const linkInterrupt = data?.linkPhase === 'telegraph';
    const meleeInterrupt = data?.attackPhase === 'windup';
    const snuffInterrupt = data?.kind === 'thrall' && data?.snuffPhase === 'channel';
    if ((linkInterrupt || meleeInterrupt || snuffInterrupt) && player.position.distanceTo(enemy.position) <= COMPANION.interruptRange) {
      if (snuffInterrupt) {
        data.snuffPhase = 'recovery';
        data.snuffTimer = SNUFFER.recovery;
      } else if (linkInterrupt) {
        data.linkPhase = 'recovery';
        data.linkTimer = MOONBOUND.recovery;
        data.linkCooldown = MOONBOUND.cooldown;
      } else {
        data.attackPhase = 'recovery';
        data.attackTimer = MELEE.recovery;
        data.attackCooldown = 1.1;
      }
      target.interrupted = enemy;
    }
  }
  // The litany's reach is the tier's reach. An explicit `revealDuration` still wins, so a caller
  // that wants a one-off reveal length can ask for one, but the default is the bond rather than
  // the flat number the profile used to hand back and nothing read.
  const reach = Number.isFinite(revealDuration) ? revealDuration : profile.revealDuration;
  return { ok: true, target, revealedUntil: now + reach, cooldown: profile.cooldown, bondTier: profile.tier, revealDuration: reach, interrupted: target.interrupted || null };
}

// The Lantern Line is the quest the first act already promised — "restore the lantern line" —
// turned into a thing the player builds. Five ward pylons stand along the road network; each one
// is paid for with blood and lit by holding still while the wick catches, which is loud, bright,
// and interruptible. A lit pylon is a hearth: faster stamina and blood recovery inside its ring,
// and a worse hiding place, because the light carries Rowan's scent as surely as the wolf's.
export const LANTERN_LINE = Object.freeze({
  igniteBlood: 25,
  channelSeconds: 2.2,
  auraRadius: 9,
  staminaRegenBonus: 5,
  bloodRecoveryScale: 2,
  exposureScale: 1.6,
  channelNoise: 1,
  channelDetection: 0.34,
  minBlood: 25,
});

export const LANTERN_PYLONS = Object.freeze([
  Object.freeze({ id: 'moonwell-shore', name: 'Moonwell Shore', district: 'moonwell', x: -6, z: 15, note: 'The drowned lantern is still burning under the water.' }),
  Object.freeze({ id: 'brackenwake-bell', name: 'Brackenwake Bell', district: 'brackenwake', x: 6, z: 4, note: 'The warning bell has no wick left to hang.' }),
  Object.freeze({ id: 'greyfen-crossing', name: 'Greyfen Crossing', district: 'greyfen', x: -12, z: 9, note: 'Roots took the pack posts two winters ago.' }),
  Object.freeze({ id: 'ironpass-stair', name: 'Ironpass Stair', district: 'ironpass', x: -10, z: 2, note: 'Seraphine had every lamp on the stair pulled out by the roots.' }),
  Object.freeze({ id: 'hollow-gate', name: 'Hollow Gate', district: 'hollow', x: 21, z: 1, note: 'The last pylon before a valley that keeps no census.' }),
]);

export const getPylon = (id) => LANTERN_PYLONS.find((pylon) => pylon.id === String(id)) || null;

// A save from an older build, or a hand-edited one, must not be able to conjure wards the map
// does not have. Unknown ids are dropped instead of trusted. `snuffed` is the list of pylons a
// wick-servant has put out: the pylon is out of `lit` either way, so the record is what lets the
// HUD and the prompt say *doused* instead of *never lit*, and it survives a save and a reload.
export function normalizeWardState(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const known = new Set(LANTERN_PYLONS.map((pylon) => pylon.id));
  const lit = [...new Set((Array.isArray(source.lit) ? source.lit : []).map(String))]
    .filter((id) => known.has(id))
    .sort();
  const charge = {};
  for (const pylon of LANTERN_PYLONS) {
    const stored = Number(source.charge?.[pylon.id]);
    if (lit.includes(pylon.id) || !Number.isFinite(stored)) continue;
    charge[pylon.id] = Math.min(1, Math.max(0, stored));
  }
  const snuffed = [...new Set((Array.isArray(source.snuffed) ? source.snuffed : []).map(String))]
    .filter((id) => known.has(id) && !lit.includes(id))
    .sort();
  return { lit, charge, snuffed };
}

// The per-frame ward queries run three times per fixed step, and the save-shaping normaliser above
// builds two Sets, two arrays and a sorted copy on every call. These read the stored state through a
// bit mask instead: the lit list is normalised on its way in from a save, from an ignition and from
// the forge, so one pass is enough and nothing is allocated in the fixed step.
const WARD_BITS = new Map(LANTERN_PYLONS.map((pylon, index) => [pylon.id, 1 << index]));

function wardMask(wardState) {
  const lit = Array.isArray(wardState?.lit) ? wardState.lit : null;
  if (!lit) return 0;
  let mask = 0;
  for (let index = 0; index < lit.length; index += 1) mask |= WARD_BITS.get(String(lit[index])) || 0;
  return mask;
}

export function isWardLit(wardState, id) {
  return (wardMask(wardState) & (WARD_BITS.get(String(id)) || 0)) !== 0;
}

export function getLitWardCount(wardState) {
  let mask = wardMask(wardState);
  let count = 0;
  while (mask) { count += mask & 1; mask >>>= 1; }
  return count;
}

// The hearth is the nearest lit ring, so walking the road means passing from one pylon's warmth
// into the next rather than stacking every aura the map has ever lit onto a single point.
const NO_AURA = Object.freeze({ pylonId: null, name: null, distance: Infinity, inside: false, progress: 0 });

export function getWardAura(wardState, position) {
  const mask = wardMask(wardState);
  if (!position || mask === 0) return NO_AURA;
  let nearest = null;
  let best = Infinity;
  for (let index = 0; index < LANTERN_PYLONS.length; index += 1) {
    if (!(mask & (1 << index))) continue;
    const pylon = LANTERN_PYLONS[index];
    const distance = Math.hypot(position.x - pylon.x, position.z - pylon.z);
    if (distance < best) { best = distance; nearest = pylon; }
  }
  if (!nearest) return NO_AURA;
  return {
    pylonId: nearest.id,
    name: nearest.name,
    distance: best,
    inside: best <= LANTERN_LINE.auraRadius,
    progress: Math.max(0, Math.min(1, 1 - best / LANTERN_LINE.auraRadius)),
  };
}

export function getWardStaminaRegen(baseRegen, wardState, position) {
  return (Number.isFinite(baseRegen) ? baseRegen : 0) + (getWardAura(wardState, position).inside ? LANTERN_LINE.staminaRegenBonus : 0);
}

export function getWardBloodRecoveryScale(wardState, position) {
  return getWardAura(wardState, position).inside ? LANTERN_LINE.bloodRecoveryScale : 1;
}

// A lit ring is the worst place to be seen in. This is the whole decision: the pylon that pays
// your stamina back is the pylon that takes your shadow away.
export function getWardExposureScale(wardState, position) {
  return getWardAura(wardState, position).inside ? LANTERN_LINE.exposureScale : 1;
}

export function canIgnitePylon(wardState, id, blood) {
  const ward = normalizeWardState(wardState);
  const pylon = getPylon(id);
  if (!pylon) return { ok: false, reason: 'unknown' };
  if (ward.lit.includes(pylon.id)) return { ok: false, reason: 'lit', pylon };
  if (!(Number(blood) >= LANTERN_LINE.minBlood)) return { ok: false, reason: 'blood', pylon, required: LANTERN_LINE.igniteBlood };
  return { ok: true, pylon, required: LANTERN_LINE.igniteBlood };
}

// Ignition is two phases on purpose. `advanceIgnition` only reports that the wick caught — it
// deliberately does not put the pylon in `lit`, because lighting one costs blood and writes a save,
// and a rule that lit it for free would let the channel commit a ward nobody paid for. The caller
// commits through `commitIgnition` once it has taken the payment.
export function advanceIgnition(wardState, id, held, dt) {
  const ward = normalizeWardState(wardState);
  const pylon = getPylon(id);
  if (!pylon || ward.lit.includes(pylon.id)) return { ...ward, progress: 0, caught: false, pylonId: pylon?.id ?? null };
  const step = Math.max(0, Number(dt) || 0);
  if (!held) return { ...ward, charge: { ...ward.charge, [pylon.id]: 0 }, progress: 0, caught: false, pylonId: pylon.id };
  const before = ward.charge[pylon.id] || 0;
  const after = Math.min(1, before + step / LANTERN_LINE.channelSeconds);
  if (after < 1) return { ...ward, charge: { ...ward.charge, [pylon.id]: after }, progress: after, caught: false, pylonId: pylon.id };
  const charge = { ...ward.charge };
  delete charge[pylon.id];
  return { lit: [...ward.lit], charge, progress: 1, caught: true, pylonId: pylon.id, pylon };
}

// The only path that puts a pylon in `lit`. Re-igniting a lit pylon is a no-op rather than an
// error, so a second channel racing the first cannot double-charge the player. Relighting also
// clears the doused record: the pylon was paid for twice, and the second payment is what undid it.
export function commitIgnition(wardState, id) {
  const ward = normalizeWardState(wardState);
  const pylon = getPylon(id);
  if (!pylon || ward.lit.includes(pylon.id)) return { ...ward, changed: false, pylon };
  const charge = { ...ward.charge };
  delete charge[pylon.id];
  return {
    lit: [...ward.lit, pylon.id].sort(),
    charge,
    snuffed: ward.snuffed.filter((entry) => entry !== pylon.id),
    changed: true,
    pylon,
  };
}

// The prompt states the price and the verb, so a player standing at a pylon never has to guess
// whether it is a tap, a hold, or something they cannot afford.
export function getPylonPrompt(wardState, id, blood) {
  const ward = normalizeWardState(wardState);
  const pylon = getPylon(id);
  if (!pylon) return null;
  if (ward.lit.includes(pylon.id)) return { label: `${pylon.name} burns · the line holds`, locked: false, progress: 1, lit: true, pylon };
  const gate = canIgnitePylon(ward, pylon.id, blood);
  const progress = ward.charge[pylon.id] || 0;
  const doused = ward.snuffed.includes(pylon.id);
  if (!gate.ok) return { label: `${pylon.name} · needs ${LANTERN_LINE.igniteBlood} blood`, locked: true, reason: gate.reason, progress, lit: false, snuffed: doused, pylon };
  return {
    label: doused
      ? `Relight ${pylon.name} · hold · ${LANTERN_LINE.igniteBlood} blood`
      : `Light ${pylon.name} · hold · ${LANTERN_LINE.igniteBlood} blood`,
    locked: false,
    progress,
    lit: false,
    snuffed: doused,
    pylon,
  };
}

// Act II taught the Covenant that a wick can be pulled without anyone touching the road, and a
// wick-servant is what that lesson left on it. This is the only enemy in Vespera that does not
// hunt Rowan: it walks to the nearest burning pylon, opens a visible channel, and puts the light
// out. The whole fight is that 2.6 s window — killing the servant ends it, Mara's litany ends
// it, and standing next to the pylon only makes the servant turn and swing. A doused pylon stays
// doused in the save, and relighting it costs the same 25 blood as lighting it the first time.
export const SNUFFER = Object.freeze({
  huntRange: 38,
  snuffRange: 2.4,
  channelSeconds: 2.6,
  recovery: 0.9,
  guardRange: 5.5,
  attackCooldown: 1.6,
  attackWindup: 0.34,
  damage: 10,
  xp: 30,
});

export function isPylonSnuffed(wardState, id) {
  return normalizeWardState(wardState).snuffed.includes(String(id));
}

export function getSnuffedPylonCount(wardState) {
  return normalizeWardState(wardState).snuffed.length;
}

// Nearest *lit* pylon to the servant, not to the player: the servant answers to the road, so a
// Rowan who lights a far hearth has bought the next thing this one walks toward.
export function selectSnufferTarget(wardState, position, range = SNUFFER.huntRange) {
  const ward = normalizeWardState(wardState);
  if (!position) return null;
  const reach = Math.max(0, Number(range) || 0);
  let best = null;
  let bestDistance = reach;
  for (const pylon of LANTERN_PYLONS) {
    if (!ward.lit.includes(pylon.id)) continue;
    const distance = Math.hypot(position.x - pylon.x, position.z - pylon.z);
    if (distance < bestDistance) { bestDistance = distance; best = pylon; }
  }
  return best;
}

// The one write path that takes a pylon out of the line. It is deliberately not `igniteWardPylon`'s
// inverse anywhere else: the campaign flag and the lore record are cleared by the caller, because
// "the Lantern Line is restored" is a claim about the whole road and only the caller knows the
// road is no longer whole.
export function snuffWardPylon(wardState, id) {
  const ward = normalizeWardState(wardState);
  const pylon = getPylon(id);
  if (!pylon || !ward.lit.includes(pylon.id)) return { ...ward, changed: false, pylon };
  const charge = { ...ward.charge };
  delete charge[pylon.id];
  return {
    lit: ward.lit.filter((entry) => entry !== pylon.id),
    charge,
    snuffed: [...new Set([...ward.snuffed, pylon.id])].sort(),
    changed: true,
    pylon,
  };
}

const IDLE_SNUFFER = Object.freeze({ phase: 'idle', pylonId: null, pylonName: null, x: null, z: null, remaining: 0, progress: 0, guarding: false, event: null });

function snuffReport(data, pylon, event = null) {
  const channelling = data.snuffPhase === 'channel';
  const remaining = channelling ? Math.max(0, data.snuffTimer) : 0;
  return {
    phase: data.snuffPhase,
    pylonId: pylon?.id ?? data.snuffTargetId ?? null,
    pylonName: pylon?.name ?? null,
    x: Number.isFinite(pylon?.x) ? pylon.x : null,
    z: Number.isFinite(pylon?.z) ? pylon.z : null,
    remaining,
    progress: channelling ? Math.min(1, 1 - remaining / SNUFFER.channelSeconds) : 0,
    // Guarding is the servant answering the player instead of the road. It is what makes standing
    // on the pylon a real counterplay rather than a free block: the channel pauses, and the thing
    // pausing it is now swinging at you.
    guarding: channelling && Boolean(data.snuffGuarding),
    event,
  };
}

export function updateSnuffer(data, input = {}) {
  if (!data || data.kind !== 'thrall' || data.dead) return IDLE_SNUFFER;
  const dt = bounded(input.dt, 0, MAX_GAMEPLAY_VALUE, 0);
  const ward = normalizeWardState(input.ward);
  const position = input.position ?? data.home;
  const playerPosition = input.playerPosition ?? null;
  data.snuffGuarding = false;
  if (!['idle', 'march', 'channel', 'recovery'].includes(data.snuffPhase)) data.snuffPhase = 'idle';
  if (!Number.isFinite(data.snuffTimer) || data.snuffTimer < 0) data.snuffTimer = 0;
  if (data.snuffPhase === 'recovery') {
    data.snuffTimer = Math.max(0, data.snuffTimer - dt);
    if (data.snuffTimer <= 0) { data.snuffPhase = 'idle'; data.snuffTimer = 0; }
    return snuffReport(data, null);
  }
  // A pylon that is not burning cannot be snuffed, and a pylon the player relit mid-channel
  // cannot be snuffed either. Either way the contract is void and the servant picks the next
  // hearth rather than standing over a cold bowl for the rest of the run.
  if (data.snuffTargetId && !ward.lit.includes(data.snuffTargetId)) {
    data.snuffTargetId = null;
    data.snuffPhase = 'idle';
    data.snuffTimer = 0;
  }
  if (!data.snuffTargetId) {
    const pylon = selectSnufferTarget(ward, position);
    if (!pylon) return snuffReport(data, null);
    data.snuffTargetId = pylon.id;
    data.snuffPhase = 'march';
  }
  const pylon = getPylon(data.snuffTargetId);
  if (!pylon) { data.snuffTargetId = null; data.snuffPhase = 'idle'; return snuffReport(data, null); }
  const distance = Math.hypot(position.x - pylon.x, position.z - pylon.z);
  if (data.snuffPhase === 'channel') {
    // Knocked off the bowl, or the pylon relit under it: the channel restarts from full rather
    // than resuming, so a single swing cannot bank a partial snuff.
    if (distance > SNUFFER.snuffRange * 1.9) { data.snuffPhase = 'march'; data.snuffTimer = 0; return snuffReport(data, pylon); }
    data.snuffGuarding = Boolean(playerPosition)
      && Math.hypot(playerPosition.x - pylon.x, playerPosition.z - pylon.z) <= SNUFFER.guardRange;
    if (!data.snuffGuarding) {
      data.snuffTimer = Math.max(0, data.snuffTimer - dt);
      if (data.snuffTimer <= 0) {
        data.snuffCount = (data.snuffCount || 0) + 1;
        data.snuffPhase = 'recovery';
        data.snuffTimer = SNUFFER.recovery;
        return snuffReport(data, pylon, { type: 'snuffed', pylonId: pylon.id, name: pylon.name });
      }
    }
    return snuffReport(data, pylon);
  }
  if (distance <= SNUFFER.snuffRange) {
    data.snuffPhase = 'channel';
    data.snuffTimer = SNUFFER.channelSeconds;
    return snuffReport(data, pylon, { type: 'snuff-start', pylonId: pylon.id, name: pylon.name, seconds: SNUFFER.channelSeconds });
  }
  return snuffReport(data, pylon);
}
